import { app } from '@awork/environments/environment'
import type { Apps } from '@awork/environments/types'
import type { AccountState, ISOLanguage } from '@awork/_shared/models/account.model'
import { PersistState } from '@awork/core/state/signal-store/persistState'
import type { AppState } from '@awork/core/state/app.store'

export type Language = 'en' | 'de'

export interface LanguageObj {
  language: Language
  iso: ISOLanguage
}

interface StoredLanguage {
  appLanguage?: ISOLanguage
  accountLanguage?: ISOLanguage
}

/**
 * Normalizes unknown language storage payloads into a valid language object.
 * Uses the app's fallback when the saved preference is missing or malformed.
 * @param {unknown} value
 * @param {LanguageObj} fallback The app's language before reading the saved preference.
 * @returns {LanguageObj}
 */
function normalizeLanguageObj(value: unknown, fallback: LanguageObj): LanguageObj {
  if (!value || typeof value !== 'object') {
    return fallback
  }

  const parsedValue = value as Partial<LanguageObj>

  if (parsedValue.language === 'de' || parsedValue.iso === 'de-DE') {
    return { language: 'de', iso: 'de-DE' }
  }

  if (parsedValue.language === 'en' || parsedValue.iso === 'en-GB') {
    return { language: 'en', iso: 'en-GB' }
  }

  return fallback
}

/**
 * Gets the language from localStorage.
 * @param {false} iso
 * @returns {Language}
 */
export default function getLanguage(iso?: false): Language
export default function getLanguage(iso?: true): ISOLanguage
export default function getLanguage(iso = false): Language | ISOLanguage {
  return getLanguageForApp(app, iso)
}

/**
 * Gets the language for a specific app target.
 * @param {Apps} appTarget The app target that chooses the fallback language.
 * @param {boolean} iso Whether to return the ISO language code.
 * @returns {Language | ISOLanguage}
 */
export function getLanguageForApp(appTarget: Apps, iso?: false): Language
export function getLanguageForApp(appTarget: Apps, iso?: true): ISOLanguage
export function getLanguageForApp(appTarget: Apps, iso: boolean): Language | ISOLanguage
export function getLanguageForApp(appTarget: Apps, iso = false): Language | ISOLanguage {
  const shouldReadLocalStorage = appTarget === 'web' || appTarget === 'mobile'
  let storedLang: LanguageObj = appTarget === 'web' ? { language: 'en', iso: 'en-GB' } : getDeviceLanguage()

  if (shouldReadLocalStorage) {
    // Try to get the language from the localStorage
    try {
      const store = localStorage.getItem('awLanguage')

      storedLang = store ? normalizeLanguageObj(JSON.parse(store), storedLang) : storedLang
    } catch (_) {}
  }

  return iso ? storedLang.iso : storedLang.language
}

/**
 * Sets the language according to queryParam, store or browser language.
 * Also, it store it in localStorage
 * @returns {Promise<LanguageObj>}
 */
export async function setLanguage(): Promise<LanguageObj> {
  const urlLang = new URL(location.href).searchParams.get('lang')?.toLowerCase() as Language | undefined
  const browserLang = window.navigator.language?.toLowerCase() as ISOLanguage | Language
  const storage = await getStorage()
  let accountLang: ISOLanguage | undefined
  let appLang: Language | undefined
  let localStoredLang: ISOLanguage | undefined

  // Try to get the stored language
  if (localStorage.getItem('awLanguage')) {
    try {
      const store = localStorage.getItem('awLanguage')
      const localStoredLangObj: LanguageObj = JSON.parse(store)

      localStoredLang = localStoredLangObj?.iso?.toLowerCase() as ISOLanguage | undefined
    } catch (_) {}
  }

  if ((storage.app || storage.account !== null) && !localStoredLang) {
    const storedLanguage = await getStoredLanguage(storage)

    accountLang = storedLanguage.accountLanguage?.toLowerCase() as ISOLanguage | undefined
    appLang = storedLanguage.appLanguage?.toLowerCase() as Language | undefined
  }

  const langObj = determineLanguage(urlLang, localStoredLang, accountLang, appLang, browserLang)

  // Store the language
  try {
    localStorage.setItem('awLanguage', JSON.stringify(langObj))
  } catch (_) {}

  return langObj
}

/**
 * Determines the language based on the queryParam, store or browser language.
 * @param {Language | undefined} urlLang
 * @param {ISOLanguage | undefined} localStoredLang
 * @param {ISOLanguage | undefined} accountLang
 * @param {Language | undefined} appLang
 * @param {ISOLanguage | Language | undefined} browserLang
 * @returns {LanguageObj}
 */
function determineLanguage(
  urlLang: Language | undefined,
  localStoredLang: ISOLanguage | undefined,
  accountLang: ISOLanguage | undefined,
  appLang: Language | undefined,
  browserLang: ISOLanguage | Language | undefined
): LanguageObj {
  const langObj: LanguageObj = {
    language: 'en',
    iso: 'en-GB'
  }

  if (
    urlLang !== null ||
    localStoredLang !== undefined ||
    accountLang !== undefined ||
    appLang !== undefined ||
    browserLang !== undefined
  ) {
    if (
      ['de', 'de-at', 'de-ch', 'de-de', 'de-li', 'de-lu'].includes(
        urlLang || localStoredLang || accountLang || appLang || browserLang
      )
    ) {
      langObj.language = 'de'
      langObj.iso = 'de-DE'
    }
  }

  return langObj
}

/**
 * Gets the stored language
 * @param {{ account: string; app: string; }} storage
 * @returns {Promise<StoredLanguage>}
 */
async function getStoredLanguage(storage: { account: string; app: string }): Promise<StoredLanguage> {
  const storedLanguage: StoredLanguage = {}

  try {
    const account: AccountState = typeof storage.account === 'string' ? JSON.parse(storage.account) : storage?.account
    const app: AppState = typeof storage.app === 'string' ? JSON.parse(storage.app) : storage?.app

    if (app) {
      storedLanguage.appLanguage = app.language
    }

    if (account) {
      storedLanguage.accountLanguage = account.language
    }
  } catch (_) {}

  return storedLanguage
}

/**
 * Gets the device language
 * @returns {LanguageObj}
 */
function getDeviceLanguage(): LanguageObj {
  const langObj: LanguageObj = {
    language: 'en',
    iso: 'en-GB'
  }

  const deviceLanguage = window.navigator.language.toLowerCase()

  if (deviceLanguage) {
    if (['de', 'de-at', 'de-ch', 'de-de', 'de-li', 'de-lu'].includes(deviceLanguage)) {
      langObj.language = 'de'
      langObj.iso = 'de-DE'
    }
  }

  return langObj
}

/**
 * Gets the account and app stores
 * @returns
 */
async function getStorage() {
  const isIndexedDBCapable = PersistState.isIndexedDBCapable()

  if (isIndexedDBCapable) {
    const accountStore = await PersistState.instance?.getItem('account')
    const appStore = await PersistState.instance?.getItem('app')

    return { account: accountStore?.['state'], app: appStore?.['state'] }
  } else {
    const accountStore = localStorage.getItem('account')
    const appStore = localStorage.getItem('app')

    return { account: accountStore?.['state'], app: appStore?.['state'] }
  }
}

/**
 * Gets the date format according to the language
 * @param {boolean} [showTime=false] True to show the datetime, false to show only date
 * @param {boolean} [showYear=true] True to show the year, false to hide year
 * @returns {string}
 */
export function getDateFormat(showTime = false, showYear = true): string {
  const lang = getLanguage()
  const yearFormat = showYear ? `${getDateSeparator()}yy` : ''

  if (lang === 'de') {
    return showTime ? `dd.MM${yearFormat} H:mm` : `dd.MM${yearFormat}`
  } else {
    return showTime ? `dd/MM${yearFormat} h:mm a` : `dd/MM${yearFormat}`
  }
}

/**
 * Gets the datetime format used in file names according to the language.
 * @returns {string}
 */
export function getFileNameDateTimeFormat(): string {
  const lang = getLanguage()
  return lang === 'de' ? 'dd.MM.yy H.mm' : 'yyyy-MM-dd h-mm a'
}

/**
 * Gets the date separator according to the language
 * @returns {string}
 */
export function getDateSeparator(): string {
  const lang = getLanguage()

  return lang === 'de' ? '.' : '/'
}
